import styled from "styled-components";
import theme from "../../style/theme";
import { useState } from "react";
import { IconButton, Popover } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { GeneralTextField } from "../text-field/GeneralTextField";

interface Props {
    onBulkServiceTimeUpdate?: (pickUpTime: string, dropOffTime: string) => void;
}

export const RoutingServiceTaskTableHeader: React.FC<Props> = ({ onBulkServiceTimeUpdate }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [pickUpTime, setPickUpTime] = useState("");
    const [dropOffTime, setDropOffTime] = useState("");

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleApply = () => {
        if (onBulkServiceTimeUpdate) {
            onBulkServiceTimeUpdate(pickUpTime, dropOffTime);
        }
        handleClose();
    };

    return (
        <TableRow>
            <TableHead>
                <FirstContainer>
                    <Label>물류</Label>
                </FirstContainer>
            </TableHead>
            <TableHead>
                <SecondContainer></SecondContainer>
            </TableHead>
            <TableHead>
                <MiddleContainer>
                    <Label>주소</Label>
                </MiddleContainer>
            </TableHead>
            <TableHead>
                <MiddleContainer>
                    <Label>도착 가능 시간 범위</Label>
                </MiddleContainer>
            </TableHead>
            <TableHead>
                <MiddleContainer>
                    <ServiceTimeContainer>
                        <Label>소요시간</Label>
                        <IconButton size="small" onClick={handleClick}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </ServiceTimeContainer>
                </MiddleContainer>
            </TableHead>
            <TableHead>
                <LastContainer>
                    <Label>용적량</Label>
                </LastContainer>
            </TableHead>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <PopoverContent>
                    <Title>소요시간 일괄 입력</Title>
                    <InputContainer>
                        <InputLabel>상차 소요시간 (분)</InputLabel>
                        <GeneralTextField
                            value={pickUpTime}
                            onChange={(e) => setPickUpTime(e.target.value)}
                            placeholder="상차 시간"
                            width="100%"
                        />
                    </InputContainer>
                    <InputContainer>
                        <InputLabel>하차 소요시간 (분)</InputLabel>
                        <GeneralTextField
                            value={dropOffTime}
                            onChange={(e) => setDropOffTime(e.target.value)}
                            placeholder="하차 시간"
                            width="100%"
                        />
                    </InputContainer>
                    <ButtonContainer>
                        <CancelButton onClick={handleClose}>취소</CancelButton>
                        <ApplyButton onClick={handleApply}>적용</ApplyButton>
                    </ButtonContainer>
                </PopoverContent>
            </Popover>
        </TableRow>
    );
};

const TableRow = styled.tr``;

const TableHead = styled.th`
  padding: 0;
  margin: 0;
`;

const Label = styled.div`
  margin: 0;
  padding: 0 0 0 2px;
  text-align: left;
  color: ${theme.palette.primitives.gray400};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const FirstContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 2px;
  margin: 0 10px 0 16px;
  box-sizing: border-box;
  text-align: left;
  color: ${theme.palette.primitives.gray400};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const SecondContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 2px;
  margin: 0 30px 0 10px;
  box-sizing: border-box;
  text-align: left;
  color: ${theme.palette.primitives.gray400};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const MiddleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 2px;
  margin: 0 30px;
  box-sizing: border-box;
  text-align: left;
  color: ${theme.palette.primitives.gray400};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const LastContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 2px;
  margin: 0 30px 0 16px;
  box-sizing: border-box;
  text-align: left;
  color: ${theme.palette.primitives.gray400};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const ServiceTimeContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    
    .MuiIconButton-root {
        padding: 4px;
        color: ${theme.palette.primitives.gray400};
        
        &:hover {
            background-color: ${theme.palette.primitives.gray100};
            color: ${theme.palette.primitives.gray600};
        }
    }
`;

const PopoverContent = styled.div`
    padding: 16px;
    width: 280px;
    background: ${theme.palette.primitives.white};
`;

const Title = styled.div`
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    color: ${theme.palette.primitives.gray800};
`;

const InputContainer = styled.div`
    margin-bottom: 12px;
`;

const InputLabel = styled.div`
    font-size: 14px;
    margin-bottom: 4px;
    color: ${theme.palette.primitives.gray600};
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
`;

const CancelButton = styled.button`
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    background: ${theme.palette.primitives.white};
    border: 1px solid ${theme.palette.primitives.gray300};
    color: ${theme.palette.primitives.gray600};
    
    &:hover {
        background: ${theme.palette.primitives.gray100};
    }
`;

const ApplyButton = styled.button`
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    background: ${theme.palette.primitives.yellow400};
    border: none;
    color: ${theme.palette.primitives.white};
    
    &:hover {
        background: ${theme.palette.primitives.yellow300};
    }
`;
